// Lecture des liens d'authentification Supabase arrivant dans le navigateur.
//
// Pourquoi ce fichier existe : le client supabase-js est en flux PKCE (bon choix pour l'OAuth),
// mais un lien d'invitation / de réinitialisation généré côté admin (inviteUserByEmail,
// generateLink…) redirige TOUJOURS avec les jetons dans le hash (#access_token=…), c'est-à-dire
// le flux implicite. auth-js refuse alors le hash (« Not a valid PKCE flow url ») : la session
// n'est jamais posée et la page affiche « lien invalide » alors que le jeton vient d'être consommé.
// On lit donc l'URL nous-mêmes et on pose la session avec supabase.auth.setSession.
//
// Deuxième format supporté : ?token_hash=…&type=… (template email personnalisé). Le jeton n'est
// consommé que lorsque la page appelle verifyOtp → on le fait au clic de l'utilisateur, jamais au
// chargement, pour résister aux scanners d'emails (Outlook SafeLinks…) qui pré-visitent les liens.

export type AuthCallbackType = 'invite' | 'recovery' | 'magiclink' | 'signup' | 'email_change' | 'email';

export type AuthCallback =
  | { kind: 'implicit'; access_token: string; refresh_token: string; type: AuthCallbackType }
  | { kind: 'token_hash'; token_hash: string; type: AuthCallbackType }
  | { kind: 'error'; code: string; message: string }
  | { kind: 'none' };

const strip = (s: string, c: string) => (s.startsWith(c) ? s.slice(1) : s);

export function parseAuthCallback(hash: string, search: string, defaultType: AuthCallbackType = 'invite'): AuthCallback {
  const h = new URLSearchParams(strip(hash || '', '#'));
  const q = new URLSearchParams(strip(search || '', '?'));

  const errCode = h.get('error_code') || q.get('error_code');
  const errDesc = h.get('error_description') || q.get('error_description') || h.get('error') || q.get('error');
  if (errCode || errDesc) {
    return { kind: 'error', code: errCode || h.get('error') || q.get('error') || 'unknown', message: errDesc || 'Lien invalide' };
  }

  const access = h.get('access_token');
  const refresh = h.get('refresh_token');
  if (access && refresh) {
    return { kind: 'implicit', access_token: access, refresh_token: refresh, type: (h.get('type') as AuthCallbackType) || defaultType };
  }

  const tokenHash = q.get('token_hash')?.trim() || h.get('token_hash')?.trim();
  if (tokenHash) {
    return { kind: 'token_hash', token_hash: tokenHash, type: ((q.get('type') || h.get('type')) as AuthCallbackType) || defaultType };
  }

  return { kind: 'none' };
}

/** Message utilisateur pour une erreur de lien (codes GoTrue connus). */
export function authCallbackErrorMessage(e: Extract<AuthCallback, { kind: 'error' }>): string {
  if (e.code === 'otp_expired' || /expired|invalid/i.test(e.message)) {
    return 'Ce lien a déjà été utilisé ou a expiré. Demande un nouveau lien à Hook, ou connecte-toi si ton compte est déjà activé.';
  }
  return e.message;
}
